import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CaptureOverlay } from '../shared';

interface TextEnterProps {
  taskName: string;
  prompt: string;
  onSubmit: (text: string, file: File | null) => void;
  onSaveDraft: (text: string, file: File | null) => void;
  onClose: () => void;
  initialText?: string;
  initialFile?: File;
}

const MAX_CHARS = 2000;
const ACCEPTED_TYPES = '.pdf,.txt,.doc,.docx';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const TextEnter: React.FC<TextEnterProps> = ({
  taskName,
  prompt,
  onSubmit,
  onSaveDraft,
  onClose,
  initialText,
  initialFile,
}) => {
  const [text, setText] = useState(initialText || '');
  const [file, setFile] = useState<File | null>(initialFile || null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canSubmit = text.trim().length > 0;

  const fontSize = useMemo(() => {
    const len = text.length;
    if (len <= 80) return 28;
    if (len <= 200) return 22;
    if (len <= 500) return 18;
    return 15;
  }, [text.length]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPTED_TYPES;
    input.onchange = () => {
      if (input.files && input.files[0]) setFile(input.files[0]);
    };
    input.click();
  };

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <style>{`
        .te-area { flex:1; display:flex; flex-direction:column; padding:12px 20px 0; overflow:hidden; }
        .te-prompt { font-size:13px; color:rgba(255,255,255,0.3); font-weight:500; line-height:1.5; margin-bottom:16px; padding:0 2px; }
        .te-input {
          flex:1; width:100%; background:none; border:none; outline:none; resize:none;
          color:rgba(255,255,255,0.92); font-family:inherit; line-height:1.5;
          caret-color:#D4715A; transition: font-size 0.2s ease;
        }
        .te-input::placeholder { color:rgba(255,255,255,0.15); }
        .te-bar {
          flex-shrink:0; padding:10px 20px 18px;
          display:flex; flex-direction:column; gap:8px;
        }
        .te-bar-top { display:flex; align-items:center; justify-content:space-between; }
        .te-count { font-size:10px; font-weight:600; color:rgba(255,255,255,0.25); font-variant-numeric:tabular-nums; }
        .te-attach-btn {
          background:none; border:none; cursor:pointer; padding:4px;
          display:flex; align-items:center; gap:6px;
          color:rgba(255,255,255,0.35); font-size:11px; font-weight:500;
        }
        .te-attach-btn:active { opacity:0.6; }
        .te-file-row {
          display:flex; align-items:center; gap:8px;
          background:rgba(255,255,255,0.06); border-radius:10px; padding:8px 12px;
        }
        .te-file-name { flex:1; font-size:11px; font-weight:600; color:rgba(255,255,255,0.6); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .te-file-size { font-size:10px; color:rgba(255,255,255,0.25); flex-shrink:0; }
        .te-file-rm { background:none; border:none; cursor:pointer; padding:2px; display:flex; }
        .te-file-rm:active { opacity:0.5; }
        .te-submit {
          width:100%; height:50px; border:none; border-radius:25px;
          background:white; color:#1A1814;
          font-size:14px; font-weight:700; letter-spacing:0.02em;
          cursor:pointer; display:flex; align-items:center; justify-content:center; gap:7px;
          transition: opacity 0.15s, transform 0.1s;
        }
        .te-submit:disabled { opacity:0.15; pointer-events:none; }
        .te-submit:active { transform:scale(0.98); }
        .te-draft {
          background:none; border:none; width:100%;
          color:rgba(255,255,255,0.3); font-size:12px; font-weight:600;
          padding:6px; text-align:center; cursor:pointer;
        }
        .te-draft:active { opacity:0.5; }
      `}</style>

      {/* Writing surface */}
      <div className="te-area">
        <div className="te-prompt">{prompt}</div>
        <textarea
          ref={textareaRef}
          className="te-input"
          style={{ fontSize }}
          placeholder="Start writing…"
          value={text}
          onChange={e => setText(e.target.value.slice(0, MAX_CHARS))}
        />
      </div>

      {/* Bottom bar */}
      <div className="te-bar">
        <div className="te-bar-top">
          <span className="te-count">{text.length} / {MAX_CHARS}</span>
          {!file && (
            <button className="te-attach-btn" onClick={handleFileSelect}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              Attach
            </button>
          )}
        </div>

        {file && (
          <div className="te-file-row">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <span className="te-file-name">{file.name}</span>
            <span className="te-file-size">{formatSize(file.size)}</span>
            <button className="te-file-rm" onClick={() => setFile(null)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        )}

        <button className="te-submit" disabled={!canSubmit} onClick={() => onSubmit(text, file)}>
          Submit
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1A1814" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        </button>
        <button className="te-draft" onClick={() => onSaveDraft(text, file)}>Save Draft</button>
      </div>
    </CaptureOverlay>
  );
};
